import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword, signInAnonymously,
  signOut as fbSignOut, onAuthStateChanged, updateProfile,
  sendPasswordResetEmail, type User,
} from 'firebase/auth';
import { doc, getDoc, increment, onSnapshot, setDoc, writeBatch } from 'firebase/firestore';
import { auth, dbf } from './config';
import { UNASSIGNED, type AppUser, type Invite, type Role } from '../types';

export function watchAuth(cb: (u: User | null) => void) {
  return onAuthStateChanged(auth, cb);
}

export async function loadProfile(uid: string): Promise<AppUser | null> {
  const s = await getDoc(doc(dbf, 'users', uid));
  return s.exists() ? ({ uid, ...s.data() } as AppUser) : null;
}

/**
 * Live subscription to a user's own record. A one-shot read raced signup —
 * onAuthStateChanged fires the instant the credential exists, which is before
 * signUp() has written users/{uid}, so the read came back empty and the app
 * dropped the new account straight back onto the sign-in form. Watching the
 * document instead means the profile arrives whenever it lands, and a role
 * changed in the Firestore console takes effect without a reload.
 */
/**
 * The signed-in person's profile — but only once the SERVER has it.
 *
 * With the persistent cache, signUp()'s batch lands in local state the instant it
 * is written, and a plain listener fires on that local copy before the write is
 * committed. The app then sees a complete builder and subscribes to projects —
 * but the rules evaluate every query against server state, where the profile
 * does not exist yet, so `me()` fails, the query is refused, and a refused
 * listener stays dead. The server catches up a moment later with identical data,
 * nothing re-subscribes, and a brand-new account sits on "Could not load
 * projects" until a reload. Reproduced on the emulator: 12 of 12 refused.
 *
 * So a profile this device has written but the server has not yet confirmed is
 * held back. Once an account exists on the server, pending local edits flow
 * through as normal — a profile change still shows at once, and offline.
 * `includeMetadataChanges` is what delivers the confirmation at all: the data is
 * identical, so without it the listener would never fire again.
 */
export function watchProfile(uid: string, cb: (u: AppUser | null) => void) {
  let onServer = false;
  return onSnapshot(
    doc(dbf, 'users', uid),
    { includeMetadataChanges: true },
    s => {
      if (s.exists() && !s.metadata.hasPendingWrites) onServer = true;
      if (s.exists() && !onServer) return;       // written here, not yet anywhere else
      cb(s.exists() ? ({ uid, ...s.data() } as AppUser) : null);
    },
    () => cb(null),
  );
}

export async function signIn(email: string, password: string) {
  const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  return cred.user;
}

/**
 * Self-signup always creates a tech — the lowest privilege there is. Role,
 * company and project scope are set by the contractor afterwards; the Firestore
 * rules refuse anything else, so nobody can sign themselves up as a vendor.
 *
 * Sign-up asks only what the rules need up front: the address, a password, and
 * — where the code founds an account — the company, which goes onto the code
 * (`redeemedCompany`) and every invite they send. The contact name and phone
 * come next, on the profile screen, so `name` starts empty here.
 */
export async function signUp(
  email: string, password: string, company = '', invite?: Invite | null,
) {
  const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
  const name = '';

  // With a code, standing comes from the invite and never from the form — the
  // rules check the same thing, so a doctored client gets nowhere.
  // A signup code starts an account of its own, keyed by this user's uid — unique
  // by construction, and not one anybody could have aimed at. The company is then
  // the single thing they name themselves: it is their firm, not the sender's.
  // A vendor code founds a vendor account the same way — their own uid, their
  // own name for themselves. The work is already assigned to their email; the
  // rules only let the sign-in that owns that address spend the code.
  // A vendor's admin invite (`member`) joins the vendor's existing account and
  // carries the company's key; every other vendor code founds a new one.
  const founding = !!invite?.newAccount || (invite?.role === 'vendor' && !invite?.member);
  const profile: Record<string, unknown> = invite
    ? {
        email: email.trim(), name,
        role: invite.role,
        company: founding ? company.trim() : invite.company,
        projects: invite.projects ?? [],
        accountId: founding ? cred.user.uid : (invite.accountId ?? ''),
        ...(invite.member && invite.vendorKey ? { vendorKey: invite.vendorKey } : {}),
        inviteCode: invite.code,
        createdAt: new Date().toISOString(),
      }
    : {
        email: email.trim(), name, role: 'tech' as Role,
        // Nobody's account until an invite or a contractor says otherwise.
        company, projects: [], accountId: UNASSIGNED,
        createdAt: new Date().toISOString(),
      };

  // The profile and the code's redemption go in ONE batch: the account exists
  // and the code is counted, or neither. Written as two steps, a single-use
  // code could stay open after it had already created an account. If the
  // batch is refused the Auth login exists with no profile — App lands that on
  // "not set up", where the same invite sets it up on the login that exists.
  if (invite) {
    const b = writeBatch(dbf);
    b.set(doc(dbf, 'users', cred.user.uid), profile);
    b.update(doc(dbf, 'invites', invite.code), {
      uses: increment(1), lastUsedBy: cred.user.uid, lastUsedAt: new Date().toISOString(),
      ...(founding && company.trim() && invite.role === 'vendor' ? { redeemedCompany: company.trim() } : {}),
    } as any);
    await b.commit();
  } else {
    await setDoc(doc(dbf, 'users', cred.user.uid), profile);
  }
  return { ...profile, uid: cred.user.uid } as unknown as AppUser;
}

/**
 * The profile signUp() would write for this invite — shared with the path that
 * sets up a login that already exists (removed from an account, invited back;
 * or a sign-up whose profile write was refused). Standing comes from the code.
 */
export function profileFromInvite(uid: string, email: string, company: string, invite: Invite): Record<string, unknown> {
  const founding = !!invite.newAccount || (invite.role === 'vendor' && !invite.member);
  return {
    email: email.trim(), name: '',
    role: invite.role,
    company: founding ? company.trim() : invite.company,
    projects: invite.projects ?? [],
    accountId: founding ? uid : (invite.accountId ?? ''),
    ...(invite.member && invite.vendorKey ? { vendorKey: invite.vendorKey } : {}),
    inviteCode: invite.code,
    createdAt: new Date().toISOString(),
  };
}

/** The contact name, mirrored onto the Firebase Auth record so the console and
 *  any mail Firebase sends show a person rather than an address. */
export const setDisplayName = async (name: string) => {
  if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: name.trim() });
};

/** Empty in the real app. The dev harness fills this in so the sandbox can offer
 *  one-tap logins; production therefore renders nothing. */
export const DEV_LOGINS: { email: string; label: string }[] = [];

/**
 * The tech link's sign-in: anonymous, made by the page itself, nothing to type.
 * The uid it yields is what `techSessions/{uid}` is keyed by, and it persists in
 * this browser — so the PIN is asked once per phone, not once per visit.
 * Needs Anonymous enabled under Authentication → Sign-in method in the Firebase
 * console; until it is, this throws `auth/admin-restricted-operation`.
 */
export const signInAnon = async () => (await signInAnonymously(auth)).user;

export const resetPassword = (email: string) => sendPasswordResetEmail(auth, email.trim());
export const signOut = () => fbSignOut(auth);
